/* SpawnAnything — betting mode: the game rolls two close armies, you put coins on Blue or Red and watch.
   During a bet battle god tools and possession are locked (camera, speed and styles stay free).
   Pure rules (strength estimate, odds, wallet) live in ./betOdds.js. Coins are play money in localStorage. */
import './bet.css';
import {
  MIN_COINS, armyLabel, armySummary, clampStake, loadWallet, placeBet, rollFromPool, rollMatchup, settleBet, unitStats,
} from './betOdds.js';
import BET_POOL from '../content/betPool.js';
import { generate } from '../net/generate.js';

const KEY = 'aw-bet';
const TIME_LIMIT = 300; // s of battle time; then the side with more health left wins the bet
const nf = new Intl.NumberFormat('de-DE');
const fmt = n => nf.format(Math.round(n));
const fmtOdds = o => o.toFixed(2).replace('.', ',') + '×';
const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const TEAM = ['Blau', 'Rot'];
// prompts for the occasional AI-made opponent (only when the recipe server answers)
const AI_PROMPTS = [
  'Kampfschnecke mit Ritterhelm', 'Pilz-Krieger', 'Kaktus-Gladiator', 'Toaster-Roboter', 'Wikinger-Pinguin',
  'Laser-Lama', 'Drachenhamster', 'Gummienten-Samurai', 'Brokkoli-Barbar', 'Disco-Zombie', 'Ninja-Kartoffel', 'Eisbär-Astronaut',
];

function readWallet() { try { return loadWallet(localStorage.getItem(KEY)); } catch { return loadWallet(null); } }
function saveWallet(w) { try { localStorage.setItem(KEY, JSON.stringify(w)); } catch { /* private mode: coins live for this tab only */ } }

/**
 * createBet(opts) -> { locked(), finish(info) }
 *   types, weapons, recipes     live registries (TYPES, WEAPONS, RECIPES)
 *   allowType(k), allowWeapon(w, k)   crate locks (locked content never shows up in a bet)
 *   read(t) -> cfg              current army config of team t (readCfg)
 *   apply(cfgs)                 set both armies from [type, count, weapon, size, …] arrays, count mode, no placing
 *   takeBuildings() / putBuildings(list)   park the player's buildings during the bet
 *   adopt(t, recipe) -> type    register an AI recipe as a unit type
 *   start(), info() -> lastInfo, toast(msg)
 */
export function createBet(opts) {
  const { types, weapons, recipes, allowType, allowWeapon, read, apply, takeBuildings, putBuildings, adopt, start, info, toast } = opts;
  const blocked = opts.blocked || (() => false); // e.g. multiplayer: no bets there
  const $ = s => document.querySelector(s);
  let wallet = readWallet();
  let mode = 'off';           // off | pick | run | done
  let match = null, pick = -1, stake = Math.min(100, wallet.coins), bet = null;
  let saved = null, parked = [], timer = 0, shown = null;
  let ai = { state: 'idle', ready: [] }; // AI opponents: idle | busy | off (no server)

  /* ---------- DOM ---------- */
  const openBtn = document.createElement('button');
  openBtn.className = 'bet-open'; openBtn.type = 'button';
  openBtn.title = 'Das Spiel würfelt zwei knappe Armeen, du setzt Münzen auf den Sieger';
  $('#start').after(openBtn);
  const card = document.createElement('aside');
  card.className = 'bet-card'; card.hidden = true; card.setAttribute('aria-label', 'Wett-Modus');
  document.body.appendChild(card);
  const hud = document.createElement('div');
  hud.className = 'bet-hud'; hud.hidden = true; hud.setAttribute('aria-live', 'polite');
  document.body.appendChild(hud);
  const banner = $('#banner');
  const out = document.createElement('div'); out.className = 'bet-out bet-only';
  const rain = document.createElement('div'); rain.className = 'bet-rain bet-only'; rain.setAttribute('aria-hidden', 'true');
  const bbtn = document.createElement('div'); bbtn.className = 'bbtn bet-only';
  bbtn.innerHTML = '<button class="go" id="betNext">Nächste Wette</button><button id="betQuit">Beenden</button>';
  $('#winReward').after(out); banner.append(bbtn); banner.prepend(rain);
  bbtn.querySelector('#betNext').addEventListener('click', next);
  bbtn.querySelector('#betQuit').addEventListener('click', exit);
  openBtn.addEventListener('click', enter);
  syncOpen();

  function syncOpen() { openBtn.innerHTML = `<span>Wetten</span><i class="bet-coin"></i><b>${fmt(wallet.coins)}</b>`; }
  function setWallet(w) { wallet = w; saveWallet(w); syncOpen(); }

  /* ---------- flow ---------- */
  function enter() {
    if (mode !== 'off') return;
    if (blocked()) { toast('Im Mehrspieler gibt es keine Wetten.'); return; }
    saved = [read(0), read(1)].map(c => [c.type, c.count, c.weapon, c.size, Math.round(c.t * 100), Math.round(c.width * 100), Math.round(c.head * 100)]);
    parked = takeBuildings();
    if (opts.neutral) opts.neutral(true); // flat, clear, no infection/zones: like the calibration runs
    document.body.classList.add('bet-on');
    prefetchAI();
    next();
  }
  function exit() {
    if (mode === 'off') return;
    clearInterval(timer);
    mode = 'off'; match = null; bet = null;
    document.body.classList.remove('bet-on', 'bet-run');
    banner.classList.remove('bet-res', 'bet-won', 'bet-lost');
    banner.hidden = true; card.hidden = true; hud.hidden = true;
    if (opts.neutral) opts.neutral(false);
    if (saved) apply(saved);
    putBuildings(parked); parked = []; saved = null;
  }
  function next() {
    clearInterval(timer);
    banner.hidden = true; banner.classList.remove('bet-res', 'bet-won', 'bet-lost');
    document.body.classList.remove('bet-run'); hud.hidden = true;
    mode = 'pick'; pick = -1; bet = null; shown = null;
    roll();
  }
  function roll() {
    // calibrated pool first: fought in the real simulation, the battle gets one of the measured seeds (exact odds)
    const pm = rollFromPool(Math.random, BET_POOL, { types, weapons, allowType, allowWeapon });
    if (pm) { match = pm; match.ai = -1; apply([match.a, match.b], match.seed); stake = clampStake(wallet, stake); render(); return; }
    let force = null;
    if (ai.ready.length && Math.random() < 0.35) {
      const t = Math.random() < 0.5 ? 0 : 1;
      try { force = [t, adopt(t, ai.ready.shift())]; } catch { force = null; }
      prefetchAI();
    }
    match = rollMatchup(Math.random, { types, weapons, recipes, allowType, allowWeapon, forceType: force });
    match.ai = force ? force[0] : -1;
    apply([match.a, match.b]);
    stake = clampStake(wallet, stake);
    render();
  }
  function place() {
    if (mode !== 'pick' || pick < 0) return;
    const r = placeBet(wallet, stake);
    setWallet(r.wallet);
    bet = { stake: r.stake, pick, odds: match.odds[pick] };
    mode = 'run';
    card.hidden = true;
    document.body.classList.add('bet-run');
    renderHud();
    start();
    toast(`${fmt(bet.stake)} Münzen auf ${TEAM[pick]} · Zuschauen, Gott-Werkzeuge sind gesperrt`);
    // safety net: a stalled battle is decided by the health left after TIME_LIMIT
    timer = setInterval(() => { const i = info(); if (mode === 'run' && i && i.t >= TIME_LIMIT) settle(hpLeader(i), true); }, 1000);
  }
  function hpLeader(i) {
    const S = [match.a, match.b].map(c => unitStats(types[c[0]], weapons[c[2]], c[3], c[1]));
    const h = [0, 1].map(t => (i.hp ? i.hp[t] : 0) / Math.max(1, S[t].hp * S[t].count));
    return h[0] === h[1] ? -1 : h[0] > h[1] ? 0 : 1;
  }
  /** called by the game's victory banner; true = the betting banner took over */
  function finish(i) {
    if (mode === 'off') return false;
    if (mode === 'run') settle(i.winner, false);
    else if (shown) { $('#winTxt').textContent = shown[0]; $('#winTxt').style.color = shown[1]; $('#winSub').textContent = shown[2]; } // decided on time: keep that verdict
    return true;
  }
  function settle(winner, timeUp) {
    clearInterval(timer);
    // the pool knows the outcome of its seed: a different result means the game no longer matches the calibration
    if (match && match.expect !== undefined && match.expect !== winner) console.warn(`Wett-Pool: erwartet ${match.expect}, Ergebnis ${winner} (${match.title}, Seed ${match.seed}) – server/build-bet-pool.js neu ausführen`);
    const before = wallet.record, r = settleBet(wallet, bet, winner);
    setWallet(r.wallet);
    mode = 'done'; hud.hidden = true;
    const won = winner >= 0 && winner === bet.pick;
    $('#winTxt').textContent = winner === 0 ? 'Blau gewinnt' : winner === 1 ? 'Rot gewinnt' : 'Unentschieden';
    $('#winTxt').style.color = winner === 0 ? 'var(--blue-l)' : winner === 1 ? 'var(--red-l)' : 'var(--fg)';
    if (timeUp) $('#winSub').textContent = `Zeit abgelaufen: ${winner < 0 ? 'beide Seiten gleich stark' : TEAM[winner] + ' hat mehr Lebenspunkte übrig'}`;
    $('#winReward').hidden = true;
    shown = [$('#winTxt').textContent, $('#winTxt').style.color, $('#winSub').textContent];
    const head = winner < 0 ? `Einsatz zurück: ${fmt(bet.stake)} Münzen` : won ? `Gewonnen! +${fmt(r.net)} Münzen` : `Verloren: −${fmt(bet.stake)} Münzen`;
    const notes = [];
    if (won && wallet.streak > 1) notes.push(`Serie: ${wallet.streak} Siege in Folge`);
    if (wallet.record > before) notes.push('Neuer Rekord!');
    if (r.rescued) notes.push(`Notgroschen: zurück auf ${fmt(MIN_COINS)} Münzen`);
    out.innerHTML = `<b class="bet-net ${won ? 'up' : winner < 0 ? '' : 'down'}">${esc(head)}</b>`
      + `<span class="bet-meta"><i class="bet-coin"></i>${fmt(wallet.coins)} · Beste Serie ${fmt(wallet.bestStreak)} · Rekord ${fmt(wallet.record)}</span>`
      + (notes.length ? `<span class="bet-note">${esc(notes.join(' · '))}</span>` : '');
    rain.innerHTML = won ? Array.from({ length: 22 }, (_, k) => `<i style="left:${(k * 37 + 11) % 100}%;animation-delay:${((k * 7) % 12) / 10}s;--d:${1.4 + (k % 5) * 0.18}s"></i>`).join('') : '';
    banner.classList.remove('bet-won', 'bet-lost'); void banner.offsetWidth; // restart the pop animation
    banner.classList.add('bet-res', won ? 'bet-won' : 'bet-lost');
    banner.hidden = false;
    bbtn.querySelector('#betNext').focus();
  }

  /* ---------- AI opponents (optional: library / cached AI figures from /api/generate) ---------- */
  function prefetchAI() {
    if (BET_POOL && BET_POOL.entries && BET_POOL.entries.length) return; // calibrated bets only use measured units
    if (ai.state !== 'idle' || ai.ready.length >= 1) return;
    ai.state = 'busy';
    const p = AI_PROMPTS[(Math.random() * AI_PROMPTS.length) | 0];
    // allowModel false: betting never pays for a new model figure (only library and cache hits, see shop pricing)
    generate('unit', p, { timeoutMs: 45000, allowModel: false }).then(r => {
      if ((r.source !== 'model' && r.source !== 'library') || !r.recipe) { ai.state = 'off'; return; } // no server / no hit: never ask again
      ai.ready.push(r.recipe); ai.state = 'idle';
    }, () => { ai.state = 'off'; });
  }

  /* ---------- rendering ---------- */
  function sideHtml(t) {
    const c = t ? match.b : match.a, E = t ? match.a : match.b;
    const S = unitStats(types[c[0]], weapons[c[2]], c[3], c[1]), SE = unitStats(types[E[0]], weapons[E[2]], E[3], E[1]);
    const sum = armySummary(S, SE), p = t ? 1 - match.pBlue : match.pBlue;
    return `<button class="bet-side t${t}" data-bet-pick="${t}" aria-pressed="${pick === t}">
      <span class="bet-team">${TEAM[t]}${match.ai === t ? ' <em class="soon">KI</em>' : ''}</span>
      <b class="bet-army">${esc(armyLabel(types, c))}</b>
      <span class="bet-wp">${esc(weapons[c[2]].name)}</span>
      <span class="bet-st">HP ${fmt(sum.hp)}<br>Schaden ${fmt(sum.dps)}/s<br>${S.melee ? 'Nahkampf' : 'Reichweite ' + fmt(sum.reach) + ' m'}${sum.area ? ' · Fläche' : ''}</span>
      <span class="bet-odds">${fmtOdds(match.odds[t])}</span>
      <span class="bet-p">${Math.round(p * 100)} % Siegchance</span>
    </button>`;
  }
  function render() {
    if (mode !== 'pick' || !match) return;
    const pB = Math.round(match.pBlue * 100);
    card.innerHTML = `
      <div class="bet-headrow"><span class="h">Wett-Modus</span><span class="bet-bal"><i class="bet-coin"></i><b>${fmt(wallet.coins)}</b></span></div>
      <p class="bet-title">${match.absurd ? '<em class="soon">Absurd</em> ' : ''}${esc(match.title)}</p>
      <div class="bet-sides">${sideHtml(0)}${sideHtml(1)}</div>
      <div class="bet-bar" title="Geschätzte Siegchance"><i style="width:${pB}%"></i><i style="width:${100 - pB}%"></i></div>
      <label class="h" for="betStake">Einsatz</label>
      <div class="bet-stake">
        <div class="seg" role="group" aria-label="Einsatz wählen">
          <button data-bet-frac="0.1">10 %</button><button data-bet-frac="0.25">25 %</button><button data-bet-frac="0.5">50 %</button><button data-bet-frac="1">Alles</button>
        </div>
        <input type="number" id="betStake" min="1" max="${wallet.coins}" step="1" value="${stake}" inputmode="numeric">
      </div>
      <p class="sdesc" id="betGain"></p>
      <button class="go" id="betGo">Wette platzieren</button>
      <div class="row bet-row"><button class="ghost" id="betReroll" title="Neue Paarung würfeln">Neu würfeln</button><button class="ghost" id="betExit">Beenden</button></div>
      <p class="sdesc bet-rec">Serie ${fmt(wallet.streak)} · Beste Serie ${fmt(wallet.bestStreak)} · Rekord ${fmt(wallet.record)} · ${fmt(wallet.wins)} gewonnen, ${fmt(wallet.losses)} verloren</p>
      <p class="sdesc">${match.seed ? 'Quoten aus echten Probe-Schlachten: genau diese Paarung wurde vorab mehrfach ausgekämpft.' : 'Quoten aus einer Stärke-Schätzung (Lebenspunkte, Schaden, Reichweite, Flächenschaden, Masse).'} Während der Schlacht nur zuschauen.</p>`;
    card.querySelectorAll('[data-bet-pick]').forEach(b => b.addEventListener('click', () => { pick = +b.dataset.betPick; syncPick(); }));
    card.querySelectorAll('[data-bet-frac]').forEach(b => b.addEventListener('click', () => { setStake(wallet.coins * +b.dataset.betFrac); card.querySelector('#betStake').value = stake; }));
    card.querySelector('#betStake').addEventListener('input', e => { setStake(e.target.value); });
    card.querySelector('#betStake').addEventListener('change', e => { e.target.value = stake; });
    card.querySelector('#betGo').addEventListener('click', place);
    card.querySelector('#betReroll').addEventListener('click', () => { pick = -1; roll(); });
    card.querySelector('#betExit').addEventListener('click', exit);
    card.hidden = false;
    syncPick();
  }
  function setStake(v) { stake = clampStake(wallet, Math.ceil(+v || 0)); syncPick(); }
  function syncPick() {
    card.querySelectorAll('[data-bet-pick]').forEach(b => b.setAttribute('aria-pressed', String(+b.dataset.betPick === pick)));
    const go = card.querySelector('#betGo'), gain = card.querySelector('#betGain');
    if (!go) return;
    go.disabled = pick < 0;
    gain.textContent = pick < 0 ? 'Tippe auf Blau oder Rot.' : `${fmt(stake)} auf ${TEAM[pick]} · bei Sieg ${fmt(Math.round(stake * match.odds[pick]))} Münzen zurück (+${fmt(Math.round(stake * match.odds[pick]) - stake)})`;
  }
  function renderHud() {
    hud.innerHTML = `<i class="bet-coin"></i><span>${fmt(bet.stake)} auf <b class="t${bet.pick}">${TEAM[bet.pick]}</b> · Quote ${fmtOdds(bet.odds)} · Gewinn +${fmt(Math.round(bet.stake * bet.odds) - bet.stake)}</span><small>Nur zuschauen</small>`;
    hud.hidden = false;
  }

  /** leave the betting mode at once (multiplayer match or replay starts): a running bet is refunded */
  function cancel() {
    if (mode === 'off') return;
    if (mode === 'run' && bet) setWallet(settleBet(wallet, bet, -1).wallet);
    exit();
  }

  return {
    /** true while betting: god tools and taking over units are off */
    locked: () => mode !== 'off',
    finish, cancel,
    /** the player gives up (pause menu): a running bet counts as lost, nothing is refunded */
    forfeit() { if (mode !== 'off') exit(); },
  };
}
